import React, { useRef, useEffect, memo } from 'react';
import { NOT_FOUND } from 'redux-first-router';
import { connect } from 'react-redux';
import UniversalComponent from './UniversalComponent';
import ErrorBoundary from '../hocs/ErrorBoundary/ErrorBoundary';
import { addClass, removeClass, addScript } from '../utils/dom';
import { removeLanguagePrefix } from '../utils/locale';
import { useLanguageURLRoots } from '../config';

const getCurrentPath = (setup, pathname, type) => {
	const path = decodeURIComponent(pathname);
	let currentPath = path.indexOf('#') !== -1 ? path.slice(0, path.indexOf('#')) : path;
	currentPath = Object.keys(setup.languages).length > 1 && !useLanguageURLRoots ? removeLanguagePrefix(currentPath) : currentPath.replace(/^\//, '');
	if (currentPath === '') currentPath = 'start';
	if (type === NOT_FOUND) currentPath = '404';

	return currentPath;
};

const areEqual = (prevProps, { pageStatus, location, pageData, properties, setup, productCache = {} }) => {
	if (pageStatus.loading) addClass(document.body, 'is_pageLoading');
	if (pageStatus.loading || properties.siteInitiated === false) return true;

	const currentPath = getCurrentPath(setup, location.pathname, location.type);
	const data = pageData[currentPath];
	const staticPages = ['receipt', 'selection/receipt', 'selection/tracking', 'selection/receipt/debug', 'search', 'selection', 'remember', 'account', 'accountOrder', 'logout', 'favourites', '404'];

	// Check if all data is available.
	if ((!pageData || !data) && staticPages.indexOf(pageStatus.pageType) === -1) return true;
	// Here we want to intercept "CatchAll" and read path API data to know which page to load.
	if (pageStatus.pageType === 'category' && Object.keys(productCache).length === 0) return true;

	if (pageStatus.pageType !== 'category') window.scrollTo(0, 0);
	removeClass(document.body, 'is_pageLoading');

	return false;
};

const Page = ({ setup, location, pageStatus, pageData, missingPage }) => {
	const { pathname, type, payload = {} } = location;
	const currentPath = getCurrentPath(setup, pathname, type);
	const data = type === NOT_FOUND ? { content: missingPage } : pageData[currentPath];
	let page = data?.pageType || pageStatus?.pageType;
	if (type === 'START') page = 'start';

	const parentContainer = useRef(null);
	const components = {
		start: 'Page/Page',
		page: 'Page/Page',
		404: 'Page/Page',
		product: 'Product/Product',
		category: 'Category/Category',
		selection: 'Selection/Selection',
		account: 'Account/Account',
		accountOrder: 'Account/Authenticated/Order',
		favourites: 'Favourites/Favourites',
		remember: 'Account/Remember',
		'selection/failed': 'Page/Page',
		'selection/receipt': 'Selection/Receipt',
		'selection/receipt/debug': 'Selection/Receipt',
	};

	useEffect(() => {
		if (page === 'selection/tracking') {
			addScript('https://cdn.ingrid.com/delivery-tracking/bootstrap.js').then(() => {
				if (typeof window.IngridDeliveryTrackingWidgetApi !== 'undefined') {
					if (Object.keys(payload).length > 0 && payload.orderId && payload.email) {
						window.IngridDeliveryTrackingWidgetApi.renderPrivateMode({
							containerId: 'js-ingrid-tracking-widget',
							siteId: '2952cc85-9c1a-49e3-af84-cc392496f3f8',
							identifier: payload.orderId,
							contact: payload.email,
						});
					} else {
						window.IngridDeliveryTrackingWidgetApi.renderSearchMode({
							containerId: 'js-ingrid-tracking-widget',
							siteId: '2952cc85-9c1a-49e3-af84-cc392496f3f8',
						});
					}
				}
				return 1;
			}).catch(() => {
				console.log('DEBUG: failed to load ingrid script');
			});
		}
	}, [page]);

	return (
		<div ref={parentContainer}>
			{ page === 'selection/tracking' && <div id="js-ingrid-tracking-widget" className="u_ingridTrackingWidget" /> }
			{ components[page] && <UniversalComponent page={components[page]} pageType={page} data={data} currentPath={currentPath} /> }
		</div>
	);
};

const Switcher = ({ pageStatus, ...props }) => {
	if (pageStatus.loading) return true;
	return (
		<ErrorBoundary message="Error in Switcher">
			<Page pageStatus={pageStatus} {...props} />
		</ErrorBoundary>
	);
};

const mapState = ({ pageStatus, location, pageData, environment, productCache }) => ({
	pageStatus,
	location,
	pageData,
	properties: environment.properties,
	setup: environment.setup,
	missingPage: environment.missingPage,
	productCache,
});

export default connect(mapState)(memo(Switcher, areEqual));
